import React, { useEffect, useState } from "react";
import "./Profile.css";
import { useAuth } from "../../utils/AuthContext.jsx";
import Navbar from "../../components/Navbar/Navbar.jsx";
import profileIcon from "../../assets/profileIconSVG.svg";
import { useNavigate } from "react-router-dom";
import { getBaseUrl } from "../../utils/getRedirectUrl";

export default function Account() {
    const {
        user,
        logoutUser,
        getLicense,
        license,
        isTrialExpired,
        nextPayment,
        deleteAccount,
        cancelSubscription,
    } = useAuth();
    const navigate = useNavigate();
    const [activePopup, setActivePopup] = useState(null);
    // Helper to extract and capitalize name from email
    const getNameFromEmail = (user) => {
        if (user.name) return user.name;
        if (!user.email) return "Name";
        const namePart = user.email.split("@")[0];
        if (!namePart) return "Name";
        return namePart.charAt(0).toUpperCase() + namePart.slice(1);
    };

    // console.log("user: ", user);

    useEffect(() => {
        if (license === "Loading...") {
            getLicense();
        }
        // eslint-disable-next-line
    }, []);

    const formatLicense = (license) => {
        if (license === "free") {
            if (isTrialExpired) {
                return "Free"; // expired
            } else {
                return "Free";
            }
        } else if (license === "masterMonthly" || license === "masterYearly") {
            return "Master";
        } else {
            return "Loading...";
        }
    };

    const handleDeleteAccount = async () => {
        const accountDeleted = await deleteAccount();
        if (accountDeleted) {
            window.alert(
                "Your account and all associated data have been deleted."
            );
            navigate("/");
        }
    };

    const handleCancelSubscription = async () => {
        const subscriptionCanceled = await cancelSubscription();
        console.log("Subscription canceled:", subscriptionCanceled);
        if (subscriptionCanceled) {
            window.alert("Your subscription has been succesfully canceled.");
            window.location.reload();
        }
    };

    const AccountPopup = ({ activePopup, setActivePopup }) => {
        if (activePopup === null) return null;

        let header = "";
        let body = "";
        let confirmText = "";
        let onConfirm = () => {};

        if (activePopup === "deleteAccount") {
            header = "Confirm Account Deletion";
            body =
                "Your account will be permanently deleted, and all associated data will be lost. This action cannot be undone.";
            confirmText = "Delete Account";
            onConfirm = handleDeleteAccount;
        } else if (activePopup === "cancelSubscription") {
            header = "Cancel Subscription";
            body =
                "Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.";
            confirmText = "Cancel Subscription";
            onConfirm = handleCancelSubscription;
        }

        return (
            <div className="popup">
                <div className="popup-content">
                    <div className="popup-header">
                        <h2>{header}</h2>
                    </div>
                    <div className="popup-body">
                        <p>{body}</p>
                    </div>
                    <div className="popup-footer">
                        <button
                            className="popup-confirm-button"
                            onClick={onConfirm}
                        >
                            {confirmText}
                        </button>
                        <button
                            className="popup-close-button"
                            onClick={() => setActivePopup(null)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <title>Your Account | Oakmate</title>
            <meta
                name="description"
                content="Manage your Oakmate profile, settings, and license information."
            />
            <div className="accountPage">
                <AccountPopup
                    activePopup={activePopup}
                    setActivePopup={setActivePopup}
                />
                <Navbar isDownloadVisible={false} />
                <div className="accountPage-container">
                    <div className="accountPage-leftPanel">
                        <div className="profileIcon-wrapper">
                            <img
                                src={profileIcon}
                                alt="profileIcon"
                                className="profileIcon"
                            />
                        </div>
                    </div>
                    <div className="accountPage-separatorVertical" />
                    <div className="accountPage-content">
                        <div className="pageHeader">
                            <div className="page-name">My Profile</div>
                            <button
                                className="logout-button"
                                onClick={logoutUser}
                            >
                                <div className="logout-button-text">
                                    Log out
                                </div>
                            </button>
                        </div>
                        <div className="pageContent">
                            <div className="pageSection">
                                <div className="pageSection-header">
                                    Oakmate infos
                                    {license === "free" && (
                                        <button
                                            className="edit-profile"
                                            onClick={() => {
                                                window.open(
                                                    `${getBaseUrl()}?scrollTo=pricing-section`,
                                                    "_blank"
                                                );
                                            }}
                                        >
                                            <div className="edit-profile-text">
                                                Upgrade
                                            </div>
                                            <svg
                                                width="14"
                                                height="20"
                                                viewBox="0 0 14 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="edit-profile-icon"
                                            >
                                                <path
                                                    d="M11.0638 0.0850639C11.1861 0.155987 11.2808 0.266301 11.3323 0.397986C11.3838 0.52967 11.3892 0.67494 11.3475 0.810064L9.09625 8.12506H13.25C13.3721 8.12502 13.4915 8.16071 13.5935 8.22775C13.6955 8.29479 13.7757 8.39024 13.824 8.5023C13.8724 8.61437 13.887 8.73816 13.8658 8.85838C13.8446 8.9786 13.7887 9.09 13.705 9.17881L3.705 19.8038C3.60825 19.9067 3.47949 19.9738 3.33972 19.9941C3.19995 20.0145 3.05742 19.9869 2.93534 19.9158C2.81327 19.8448 2.71886 19.7345 2.66749 19.6029C2.61613 19.4714 2.61085 19.3263 2.6525 19.1913L4.90375 11.8751H0.750005C0.627936 11.8751 0.508525 11.8394 0.406513 11.7724C0.3045 11.7053 0.224351 11.6099 0.175962 11.4978C0.127572 11.3858 0.113058 11.262 0.134214 11.1417C0.155369 11.0215 0.211266 10.9101 0.295005 10.8213L10.295 0.196314C10.3916 0.0935628 10.5202 0.0265247 10.6598 0.00609958C10.7993 -0.0143255 10.9417 0.0130637 11.0638 0.0838139V0.0850639Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                <div className="account-Infos">
                                    <div className="account-info-wrapper">
                                        <div className="account-info-key">
                                            Name
                                        </div>
                                        <div className="account-info-data">
                                            {user?.email
                                                ? getNameFromEmail(user)
                                                : "Name"}
                                        </div>
                                    </div>
                                    <div className="account-info-wrapper">
                                        <div className="account-info-key">
                                            Email address
                                        </div>
                                        <div className="account-info-data">
                                            {user?.email
                                                ? user.email
                                                : "loading@gmail.com"}
                                        </div>
                                    </div>
                                    <div className="account-info-wrapper">
                                        <div className="account-info-key">
                                            Phone number
                                        </div>
                                        <div className="account-info-data">
                                            {user?.phone ? user.phone : "-"}
                                        </div>
                                    </div>
                                    <div className="account-info-wrapper">
                                        <div className="account-info-key">
                                            My license
                                        </div>
                                        <div
                                            className={`account-info-data ${
                                                license !== "free" ? "plan" : ""
                                            }`}
                                        >
                                            {license
                                                ? formatLicense(license)
                                                : "Loading..."}
                                        </div>
                                    </div>
                                    <div className="account-info-wrapper">
                                        <div className="account-info-key">
                                            Next payment
                                        </div>
                                        <div className="account-info-data">
                                            {nextPayment
                                                ? `${nextPayment.amount.toFixed(
                                                      2
                                                  )}€ due ${new Date(
                                                      nextPayment.date
                                                  ).toLocaleDateString(
                                                      "fr-FR"
                                                  )}`
                                                : license === "free"
                                                ? "-"
                                                : "Loading..."}
                                        </div>
                                    </div>
                                </div>
                                <div className="cancelSubscription">
                                    <button
                                        type="button"
                                        className="cancelSubscription-button"
                                        onClick={() => {
                                            setActivePopup("deleteAccount");
                                        }}
                                    >
                                        <div className="cancelSubscription-text">
                                            Delete Account
                                        </div>
                                    </button>
                                    {license !== "free" &&
                                        license !== "Loading..." && (
                                            <button
                                                type="button"
                                                className="cancelSubscription-button"
                                                onClick={() => {
                                                    setActivePopup(
                                                        "cancelSubscription"
                                                    );
                                                }}
                                            >
                                                <div className="cancelSubscription-text">
                                                    Cancel Subscription
                                                </div>
                                            </button>
                                        )}
                                </div>
                            </div>
                            <div className="pageSection">
                                <div className="chesscom-Infos">
                                    <div className="pageSection-header">
                                        Chess.com infos
                                    </div>
                                    <div className="account-chesscom-Infos">
                                        {user.prefs?.chesscomAvatar ? (
                                            <img
                                                src={user.prefs.chesscomAvatar}
                                                alt=""
                                                className="chesscomPP"
                                            />
                                        ) : (
                                            <svg
                                                version="1.1"
                                                id="Layer_1"
                                                xmlns="http://www.w3.org/2000/svg"
                                                x="0"
                                                y="0"
                                                viewBox="0 0 400 400"
                                                className="chesscomPP"
                                            >
                                                <path
                                                    fill="#e7e5e3"
                                                    d="M0 0h400v400H0z"
                                                />
                                                <path
                                                    fill="#898785"
                                                    d="M275.8 269.6c-52.8-40.2-46.9-75.2-47.6-89.5h32.2c3.8-6.6 5.7-14 5.7-21.5l-36.5-24c22.6-16.3 27.8-47.9 11.5-70.5-5.6-7.7-13.2-13.8-22.1-17.3-5.9-2.4-47.2 133.4-47.2 133.4-.1 3.1-.2 7.2-.2 12.1 0 13.5 33.1 11.4 31.4 23.4-2.6 17.8-3.2 31.3-18.5 74.1-10.4 28.9-79.4 0-84.3 14.2-3.6 10.6-5.3 21.7-5.2 32.8 0 1.3 2.7 20.4 105.1 20.4s105.1-19.1 105.1-20.4c-.1-29.2-10.9-53.1-29.4-67.2z"
                                                />
                                                <path
                                                    fill="#bcbbb7"
                                                    d="M198.1 287.6c5.7-25.9 10.7-53.5 13.8-70 3.8-20.6-27.5-24.3-40.3-26.2-.6 17.6-5.5 46.2-47.4 78.2-11.3 8.6-19.7 20.9-24.6 35.8 11.3 5.5 26.4 8.8 49.7 8.8 14.9 0 42.6 1.8 48.8-26.6zM220.1 180.1c5-12.9 4.3-21.5 4.3-21.5l-20.7-24c22-9.4 35.2-27 35.2-47.5 0-15.8-7.4-30.6-19.9-40.2-25.8-10.5-55.3 1.9-65.9 27.7-8.8 21.6-1.7 46.4 17.2 60l-36.5 24c0 7.6 1.9 15 5.7 21.5h80.6z"
                                                />
                                                <path
                                                    fill="#dad7d5"
                                                    d="M197.1 53.3c29.1 4.5-13.4 38.4-26.9 36.8-12.7-1.6-.4-41 26.9-36.8z"
                                                />
                                            </svg>
                                        )}
                                        <div className="account-info-wrapper">
                                            <div className="account-info-key">
                                                Username
                                            </div>
                                            <div className="account-info-data">
                                                {user.prefs?.chesscom ? (
                                                    user.prefs.chesscom
                                                ) : (
                                                    <button
                                                        className="setup-cta-profile"
                                                        onClick={() =>
                                                            navigate("/setup")
                                                        }
                                                    >
                                                        <div className="setup-cta-profile-text">
                                                            Setup
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="pageSection">
                                <div className="pageSection-header">
                                    Lichess.org infos
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
